import { useEffect, useState } from 'react';
import { InvoiceLineWithKey } from 'fetch/reportFetch';
import {
  extractMinCompThreshold,
  isBackupLine,
  isConsolidated,
  isDelivery,
  isDeliveryBonus,
  isDeviation,
  isExtraWeekendPrice,
  isExtraWork,
  isIdVerification,
  isInboundLine,
  isMileage,
  isMinComp,
  isShiftLine,
  isSortingLine,
  NO_REGION,
  NO_VEHICLE,
} from './utils';
import { roundToTwo } from '../../components/OrderListTable/utils/roundToTwo';

// Total: count * price
interface InvoiceDetails {
  count: number;
  rate: number;
  total: number;
}

export interface InvoiceShiftLine {
  price: number;
  count: number;
  invoicingRegion: string;
  vehicle: string;
}

export interface CourierInvoiceLine {
  date: string;
  deliveries: { [vehicle: string]: InvoiceDetails };
  shifts: InvoiceShiftLine[];
  consolidated: InvoiceDetails;
  region: string;
  vehicle: string;
  km: InvoiceDetails;
  bonus: InvoiceDetails;
  sorting: InvoiceDetails;
  inbound: InvoiceDetails;
  backup: InvoiceDetails;
  extras: { amount: number; descriptions: string[] };
  minComp: { amount: number; threshold?: string };
  idVerifications: InvoiceDetails;
  extraWeekendPrice: { total: number };
  deviations: { total: number };
  total: number;
}

interface InvoiceLinesByDate {
  [date: string]: InvoiceLineWithKey[];
}

export const useCourierInvoiceLines = (
  rawInvoiceLines: InvoiceLineWithKey[]
): CourierInvoiceLine[] => {
  const [invoiceLines, setInvoiceLines] = useState<CourierInvoiceLine[]>([]);

  useEffect(() => {
    const linesByDate = {} as InvoiceLinesByDate;
    rawInvoiceLines.forEach(line => {
      if (linesByDate[line.date] == null) {
        linesByDate[line.date] = [];
      }
      linesByDate[line.date].push(line);
    });

    const courierInvoiceLines = [] as CourierInvoiceLine[];
    Object.entries(linesByDate).forEach(([date, lines]) => {
      /*
       * Assumptions
       * - Per date, the current data model guarantees same operational region
       *     which is why we take lines[0].operationalRegion as region.
       * - For now, we do not need to show inbound/backup/extras based on
       *     invoicing region
       * - Sorting, inbound, backup and extras each may have multiple entries
       *      per day
       */
      let courierInvoiceLine = {
        deliveries: {},
        shifts: [],
        consolidated: { total: 0, rate: 0, count: 0 },
        vehicle: null,
        bonus: { total: 0, rate: 0, count: 0 },
        sorting: { total: 0, rate: 0, count: 0 },
        inbound: { total: 0, rate: 0, count: 0 },
        backup: { total: 0, rate: 0, count: 0 },
        extras: { amount: 0, descriptions: [] },
        km: { total: 0, rate: 0, count: 0 },
        total: 0,
        minComp: { amount: 0 },
        idVerifications: { total: 0, rate: 0, count: 0 },
        extraWeekendPrice: { total: 0 },
        deviations: { total: 0 },
        region: lines[0].operationalRegion,
        date,
      } as CourierInvoiceLine;

      lines.forEach(line => {
        const shifts = [...courierInvoiceLine.shifts];
        if (isShiftLine(line)) {
          shifts.push({
            price: line.price * 0.01,
            count: line.count,
            invoicingRegion: line.invoicingRegion ?? NO_REGION,
            vehicle: line.vehicleCategory ?? NO_VEHICLE,
          });
        }

        const consolidated = { ...courierInvoiceLine.consolidated };
        if (isConsolidated(line)) {
          consolidated.count += line.count;
          consolidated.total += line.count * line.price * 0.01;
          consolidated.rate = line.price * 0.01;
        }

        const vehicle = courierInvoiceLine.vehicle ?? line.vehicleCategory;

        const minComp = isMinComp(line)
          ? {
              amount: line.price * 0.01,
              threshold: extractMinCompThreshold(line),
            }
          : courierInvoiceLine.minComp;

        // Assume that the bonus rate is constant
        const bonus = { ...courierInvoiceLine.bonus };
        if (isDeliveryBonus(line)) {
          bonus.count += line.count;
          bonus.total += line.count * line.price * 0.01;
          bonus.rate = line.price * 0.01;
        }

        const deliveries = { ...courierInvoiceLine.deliveries };
        if (isDelivery(line) && !isDeliveryBonus(line)) {
          const vehicle = line.vehicleCategory ?? NO_VEHICLE;
          if (deliveries[vehicle] == null) {
            deliveries[vehicle] = {
              total: 0,
              rate: 0,
              count: 0,
            };
          }
          deliveries[vehicle].count += line.count;
          deliveries[vehicle].total += line.count * line.price * 0.01;
          deliveries[vehicle].rate = line.price * 0.01;
        }

        const sorting = { ...courierInvoiceLine.sorting };
        if (isSortingLine(line)) {
          sorting.count += line.count;
          sorting.total += roundToTwo(line.count * line.price * 0.01);
          sorting.rate = line.price * 0.01;
        }

        const inbound = { ...courierInvoiceLine.inbound };
        if (isExtraWork(line) && isInboundLine(line)) {
          inbound.count += line.count;
          inbound.total += line.count * line.price * 0.01;
          inbound.rate = line.price * 0.01;
        }

        const backup = { ...courierInvoiceLine.backup };
        if (isExtraWork(line) && isBackupLine(line)) {
          backup.count += line.count;
          backup.total += line.count * line.price * 0.01;
          backup.rate = line.price * 0.01;
        }

        const idVerifications = { ...courierInvoiceLine.idVerifications };
        if (isIdVerification(line)) {
          idVerifications.count += line.count;
          idVerifications.total += line.count * line.price * 0.01;
          idVerifications.rate = line.price * 0.01;
        }

        const extraWeekendPrice = { ...courierInvoiceLine.extraWeekendPrice };
        if (isExtraWeekendPrice(line)) {
          extraWeekendPrice.total += line.price * line.count * 0.01;
        }

        const deviations = { ...courierInvoiceLine.deviations };
        if (isDeviation(line)) {
          deviations.total += line.price * 0.01;
        }

        const km = { ...courierInvoiceLine.km };
        if (isMileage(line)) {
          km.count += line.count;
          km.total += line.count * line.price * 0.01;
          km.rate = line.price * 0.01;
        }

        const total = courierInvoiceLine.total + line.count * line.price * 0.01;

        const extras = { ...courierInvoiceLine.extras };
        if (
          isExtraWork(line) &&
          !isSortingLine(line) &&
          !isInboundLine(line) &&
          !isBackupLine(line)
        ) {
          extras.amount += line.count * line.price * 0.01;
          extras.descriptions.push(line.description);
        }

        courierInvoiceLine = {
          ...courierInvoiceLine,
          deliveries,
          shifts,
          consolidated,
          bonus,
          vehicle,
          km,
          sorting,
          inbound,
          backup,
          extras,
          minComp,
          idVerifications,
          extraWeekendPrice,
          deviations,
          total,
        };
      });

      courierInvoiceLines.push(courierInvoiceLine);
    });

    setInvoiceLines(courierInvoiceLines);
  }, [rawInvoiceLines]);

  return invoiceLines;
};
